import { useLoaderData, useSearchParams } from '@remix-run/react'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import PlentyBody from '#app/components/typography/plenty-body'
import PlentyHeading from '#app/components/typography/plenty-heading'
import { Icon } from '#app/components/ui/icon'
import { Label } from '#app/components/ui/label'
import {
	Modal,
	ModalContent,
	ModalHeader,
	ModalTitle,
} from '#app/components/ui/modal'
import { RadioGroup, RadioGroupItem } from '#app/components/ui/radio-group'
import { LoadingSpinner } from '#app/components/ui/spinner'
import { useFetcherWithReset } from '#app/hooks/useFetcherWithReset'
import {
	type loader,
	type action,
} from '#app/routes/_public+/product+/$styleNumber'
import { moneyFormat, centPriceToPrice } from '#app/utils/money'
import {
	type PlentyVariant,
	type PlentyVariantAvailability,
	type StoreAvailability,
} from '#types/product'
import NewSearchStoreInput from './new-search-store-input'

interface ChooseStoreMobileModalProps {
	firstAvailability: PlentyVariantAvailability[]
	selectedSku: string | null | undefined
	sizeOptions: Array<{
		label: string
		value: string
		disabled: boolean
		availableQuantity: number
		variant: PlentyVariant | undefined
		badgeProps?: {
			type: string
			text: string
		}
	}>
}

const SizeIndicator = ({ quantity }: { quantity: number }) => {
	if (quantity > 2) {
		return (
			<Icon
				name="check-mark-monochrome"
				className="text-mint-green-60"
				size="sm"
			/>
		)
	} else if (quantity > 0) {
		return (
			<Icon
				name="exclamation-monochrome"
				className="text-orange-90"
				size="sm"
			/>
		)
	} else {
		return <Icon name="close-monochrome" className="text-red-90" size="sm" />
	}
}

const ChooseStoreMobileModal: React.FC<ChooseStoreMobileModalProps> = ({
	firstAvailability,
	selectedSku,
	sizeOptions,
}) => {
	const { t } = useTranslation('shopping_bag')
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [selectedChannelId, setSelectedChannelId] = useState('')
	const [currentView, setCurrentView] = useState<'store' | 'size'>('store')
	const [loadingSku, setLoadingSku] = useState<string | null>(null)
	const productFetcher = useFetcherWithReset<typeof action>({ key: 'addToBag' })
	const [searchParams] = useSearchParams()
	const loaderData = useLoaderData<typeof loader>()
	const productData = loaderData?.productData
	const activeStyle = productData?.styleAvailableVariants.find(
		(style: any) => style.active,
	)

	const sortStoresByTotalQuantity = (stores: PlentyVariantAvailability[]) => {
		return [...stores].sort((a, b) => {
			const getTotalQuantity = (store: PlentyVariantAvailability) => {
				const total =
					activeStyle?.variants.reduce((total: number, variant: any) => {
						const storeAvailability = variant.availability?.find(
							(avail: any) => avail.storeKey === store.storeKey,
						)
						return total + (storeAvailability?.availableQuantity || 0)
					}, 0) || 0
				return total
			}

			const totalA = getTotalQuantity(a)
			const totalB = getTotalQuantity(b)
			return totalB - totalA
		})
	}

	const [filteredStores, setFilteredStores] = useState(() =>
		sortStoresByTotalQuantity([...firstAvailability]),
	)

	const handleSearch = (searchTerm: string) => {
		const normalizedSearch = searchTerm.toLowerCase().trim()

		if (!normalizedSearch) {
			setFilteredStores(sortStoresByTotalQuantity(firstAvailability))
			return
		}

		const filtered = firstAvailability.filter(store => {
			const storeName = store.storeName?.toLowerCase() || ''
			const address =
				`${store.storeAddress?.streetName} ${store.storeAddress?.streetNumber}, ${store.storeAddress?.postalCode} ${store.storeAddress?.city}`.toLowerCase()

			return (
				storeName.includes(normalizedSearch) ||
				address.includes(normalizedSearch)
			)
		})

		setFilteredStores(sortStoresByTotalQuantity(filtered))
	}

	useEffect(() => {
		if (productFetcher.state === 'idle' && loadingSku) {
			setLoadingSku(null)
			setIsModalOpen(false)
		}
	}, [productFetcher.state, loadingSku])

	useEffect(() => {
		if (!isModalOpen) {
			const storeInUrl = searchParams.get('store')
			if (!storeInUrl) {
				setSelectedChannelId('')
				setFilteredStores(sortStoresByTotalQuantity(firstAvailability))
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isModalOpen, searchParams, firstAvailability, activeStyle])

	const handleTriggerClick = (e: React.MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()

		// Always load the stores list first, regardless of URL parameters
		setFilteredStores(sortStoresByTotalQuantity(firstAvailability))
		setCurrentView('store')

		const storeInUrl = searchParams.get('store')
		if (storeInUrl) {
			setSelectedChannelId(storeInUrl)
			// We still set the store from URL, but we show the store view first
		} else {
			setSelectedChannelId('')
		}
		setIsModalOpen(true)
	}

	const handleSelectStore = (storeChannelKey: string) => {
		// Update the selected store
		setSelectedChannelId(storeChannelKey)
		// Move to the size view
		setCurrentView('size')
	}

	const handleSelectSize = (sku: string) => {
		if (productFetcher.state === 'idle') {
			setLoadingSku(sku)
			productFetcher.submit(
				{
					sku,
					styleNumber: productData?.productInfo.key || '',
					storeKey: selectedChannelId,
					channelId: selectedChannelId,
				},
				{
					method: 'POST',
					action: `/product/${productData?.productInfo
						.key}?${searchParams.toString()}`,
				},
			)
		}
	}

	const getSizeOptionsForStore = (
		options: typeof sizeOptions,
		storeKey: string,
	) => {
		// If we have generalAvailability, use it to get accurate store availability
		if (activeStyle?.generalAvailability) {
			return options.map(option => {
				// Use the original label for size without modification
				// This correctly handles "ONE SIZE" case
				const size = option.label
				const sizeAvailability = activeStyle.generalAvailability.bySize[size]

				if (!sizeAvailability) {
					return {
						...option,
						availableQuantity: 0,
						disabled: true,
					}
				}

				// Find this specific store's availability for this size
				const storeAvailabilityForSize =
					sizeAvailability.storeAvailability?.find(
						(store: StoreAvailability) => store.storeKey === storeKey,
					)

				const quantity = storeAvailabilityForSize?.quantity || 0

				// Check if discount should be applied for this store
				const variant = option.variant
				const shouldApplyDiscount =
					variant?.lowestPrice?.discounted?.discount?.obj?.references?.some(
						(ref: any) => ref.id === storeKey,
					)
				const badgeProps = shouldApplyDiscount ? option.badgeProps : undefined

				return {
					...option,
					badgeProps,
					availableQuantity: quantity,
					disabled: quantity <= 0,
				}
			})
		}

		// Fallback to the old method if generalAvailability is not available
		return options.map(option => {
			const variant = option.variant
			const shouldApplyDiscount =
				variant?.lowestPrice?.discounted?.discount?.obj?.references?.some(
					(ref: any) => ref.id === storeKey,
				)
			const badgeProps = shouldApplyDiscount ? option.badgeProps : undefined

			const storeAvailability = variant?.availability?.find(
				a => a.channelKey === storeKey || a.storeKey === storeKey,
			)

			const storeQuantity = storeAvailability?.availableQuantity || 0

			return {
				...option,
				badgeProps,
				availableQuantity: storeQuantity,
				disabled: storeQuantity <= 0,
			}
		})
	}

	// Helper function to check if a specific size is on sale in a specific store
	const isSizeOnSaleInStore = (size: string, storeKey: string) => {
		if (!activeStyle?.generalAvailability?.bySize || !storeKey) return false

		const sizeAvailability = activeStyle.generalAvailability.bySize[size]
		if (!sizeAvailability) return false

		const storeAvailabilityForSize = sizeAvailability.storeAvailability?.find(
			storeItem => storeItem.storeKey === storeKey,
		)

		// Ensure we return a boolean
		return !!storeAvailabilityForSize?.isOnSale
	}
	return (
		<>
			<PlentyBody
				className="cursor-pointer underline"
				size="md"
				onClick={handleTriggerClick}
			>
				{t('product_details_page:click_and_collect_in_store')}
			</PlentyBody>

			<Modal open={isModalOpen} onOpenChange={setIsModalOpen}>
				<ModalContent className="max-h-[70vh] p-0">
					<ModalHeader className="flex w-full flex-col items-start justify-between gap-4 p-4">
						<ModalTitle asChild>
							<PlentyHeading as={'h7'}>
								{currentView === 'store'
									? t('shopping_bag:select_a_store')
									: t('product_details_page:select_size_label')}
							</PlentyHeading>
						</ModalTitle>
						{currentView === 'store' && (
							<>
								<NewSearchStoreInput onSearch={handleSearch} />
								<PlentyBody size="md" className="px-4 md:px-6">
									{t('product_details_page:stores_found', {
										count: filteredStores.length,
									})}
								</PlentyBody>
							</>
						)}
					</ModalHeader>

					<div
						className={`overflow-auto transition-all duration-200 ${
							currentView === 'store' ? 'block' : 'hidden'
						}`}
					>
						<div className="m-0 flex flex-col p-0">
							<RadioGroup
								className="gap-0"
								value={selectedChannelId}
								onValueChange={handleSelectStore}
							>
								{filteredStores.map(store => {
									const address = `${store.storeAddress?.streetName} ${store.storeAddress?.streetNumber}, ${store.storeAddress?.postalCode} ${store.storeAddress?.city}`

									// Determine price info for the store
									let storeHasSale = false
									let lowestDiscountedPrice: number | null = null
									let lowestOriginalPrice: number | null = null
									let lowestRegularPrice: number | null = null

									if (activeStyle?.generalAvailability?.bySize) {
										Object.entries(
											activeStyle.generalAvailability.bySize,
										).forEach(([size, sizeData]) => {
											const storeAvailabilityForSize =
												sizeData.storeAvailability?.find(
													(s: StoreAvailability) =>
														s.storeKey === store.storeKey,
												)

											// Consider only sizes available in this store
											if (
												storeAvailabilityForSize &&
												storeAvailabilityForSize.quantity > 0
											) {
												const variant = activeStyle.variants.find(
													v => v.sku === sizeData.sku,
												)
												if (!variant?.lowestPrice) return // Skip if no price info

												const originalPrice =
													variant.lowestPrice.value.centAmount
												const discountedPrice =
													variant.lowestPrice.discounted?.value.centAmount

												// Update lowest original price seen so far
												if (
													lowestOriginalPrice === null ||
													originalPrice < lowestOriginalPrice
												) {
													lowestOriginalPrice = originalPrice
												}

												if (
													storeAvailabilityForSize.isOnSale &&
													discountedPrice
												) {
													storeHasSale = true
													// Update lowest discounted price seen so far (among sale items)
													if (
														lowestDiscountedPrice === null ||
														discountedPrice < lowestDiscountedPrice
													) {
														lowestDiscountedPrice = discountedPrice
													}
												} else {
													// Update lowest regular price seen so far (among non-sale items)
													if (
														lowestRegularPrice === null ||
														originalPrice < lowestRegularPrice
													) {
														lowestRegularPrice = originalPrice
													}
												}
											}
										})
									}

									// Determine the final lowest price to display based on sale status
									const displayPrice = storeHasSale
										? lowestDiscountedPrice
										: lowestRegularPrice
									// Use the lowest original price for the strikethrough if on sale
									const displayOriginalPrice = storeHasSale
										? lowestOriginalPrice
										: null

									const formattedPrice = displayPrice
										? moneyFormat(
												centPriceToPrice(displayPrice, 2),
												'DKK',
												'da-DK',
										  )
										: '-'
									const formattedOriginalPrice = displayOriginalPrice
										? moneyFormat(
												centPriceToPrice(displayOriginalPrice, 2),
												'DKK',
												'da-DK',
										  )
										: null

									return (
										<Label
											key={store.channelKey}
											htmlFor={store.channelKey}
											className="group border-beige-90 hover:bg-beige-50 flex cursor-pointer flex-col border-b p-2 transition-colors"
											onClick={e => {
												// If this store is already selected, move to size view directly
												if (selectedChannelId === store.channelKey) {
													e.preventDefault()
													setCurrentView('size')
												}
											}}
										>
											<div className="flex">
												<div className="shrink-0">
													<RadioGroupItem
														value={store.channelKey || ''}
														id={store.channelKey}
													/>
												</div>
												<div className="ml-4 flex w-full flex-col gap-2">
													<div>
														<PlentyBody size="md" className="font-bold">
															{store.storeName ?? t('common:unknown')}
														</PlentyBody>
														<PlentyBody size="md" className="text-black-90">
															{address}
														</PlentyBody>
														<div className="flex w-full flex-row justify-between">
															<PlentyBody size="md" className="text-black-90">
																{t('product_details_page:price_from')}
															</PlentyBody>
															<div className="flex items-center gap-1">
																{storeHasSale && formattedOriginalPrice ? (
																	<>
																		<PlentyBody
																			size="md"
																			className="text-red-90"
																		>
																			{formattedPrice}
																		</PlentyBody>
																		<PlentyBody
																			size="md"
																			className="text-black-60 line-through"
																		>
																			{formattedOriginalPrice}
																		</PlentyBody>
																	</>
																) : (
																	<PlentyBody
																		size="md"
																		className="text-black-90"
																	>
																		{formattedPrice}
																	</PlentyBody>
																)}
															</div>
														</div>
													</div>
													<div className="flex gap-2">
														{Object.entries(
															activeStyle?.generalAvailability?.bySize || {},
														).map(([size, sizeData]) => {
															// Find this store's availability for this size
															const storeAvailabilityForSize =
																sizeData.storeAvailability?.find(
																	(storeOnSize: StoreAvailability) =>
																		storeOnSize.storeKey === store.storeKey,
																)

															const quantity =
																storeAvailabilityForSize?.quantity || 0

															return (
																<div
																	key={size}
																	className="flex items-center gap-1"
																>
																	<PlentyBody size="sm">{size}</PlentyBody>
																	<SizeIndicator quantity={quantity} />
																</div>
															)
														})}
													</div>
												</div>
											</div>
										</Label>
									)
								})}
							</RadioGroup>
						</div>
					</div>

					<div
						className={`overflow-auto transition-all duration-200 ${
							currentView === 'size' ? 'block' : 'hidden'
						}`}
					>
						<div className="flex flex-col">
							{getSizeOptionsForStore(sizeOptions, selectedChannelId).map(
								option => {
									const isAvailable = !option.disabled
									const isLoading = loadingSku === option.value

									return (
										<div
											key={option.value}
											className={`group border-beige-90 hover:bg-beige-50 relative flex cursor-pointer flex-col border-b px-4 py-3 transition-colors ${
												!isAvailable ? 'cursor-not-allowed opacity-50' : ''
											} ${isLoading ? 'animate-pulse' : ''}`}
											onClick={() => {
												if (
													isAvailable &&
													!isLoading &&
													productFetcher.state === 'idle'
												) {
													handleSelectSize(option.value)
												}
											}}
										>
											<div className="flex items-center justify-between">
												<PlentyBody size="md" className="text-left">
													{option.label}
												</PlentyBody>
												<div
													className={`text-sm ${
														option.availableQuantity === 0
															? 'text-light-grey-90'
															: option.availableQuantity <= 2
															  ? 'text-red'
															  : 'text-black-90'
													}`}
												>
													{option.badgeProps ? (
														<div className="flex flex-row items-center justify-between">
															<PlentyBody
																size="md"
																className={` ${
																	option.badgeProps.type === 'sale'
																		? 'text-red-90'
																		: 'text-red'
																}`}
															>
																{option.badgeProps.text}
															</PlentyBody>
															{option.variant?.lowestPrice && (
																<div className="flex items-center gap-1">
																	{isSizeOnSaleInStore(
																		option.label,
																		selectedChannelId,
																	) && option.variant.lowestPrice.discounted ? (
																		// Item is ON SALE in this store AND has discount data -> Show SALE format
																		<>
																			<PlentyBody
																				size="md"
																				className="text-red-90"
																			>
																				{t('product_details_page:price_from')}{' '}
																				{moneyFormat(
																					centPriceToPrice(
																						option.variant.lowestPrice
																							.discounted.value.centAmount,
																						2,
																					),
																					'DKK',
																					'da-DK',
																				)}
																			</PlentyBody>
																			<PlentyBody
																				size="md"
																				className="text-black-60 line-through"
																			>
																				{moneyFormat(
																					centPriceToPrice(
																						option.variant.lowestPrice.value
																							.centAmount,
																						2,
																					),
																					'DKK',
																					'da-DK',
																				)}
																			</PlentyBody>
																		</>
																	) : (
																		// Item is NOT on sale in this store (or missing discount data) -> Show REGULAR price
																		<PlentyBody size="md">
																			{moneyFormat(
																				centPriceToPrice(
																					option.variant.lowestPrice.value
																						.centAmount,
																					2,
																				),
																				'DKK',
																				'da-DK',
																			)}
																		</PlentyBody>
																	)}
																</div>
															)}
														</div>
													) : (
														<div className="flex flex-row items-center justify-between gap-x-2">
															{option.availableQuantity <= 2 &&
															option.availableQuantity > 0 ? (
																<PlentyBody size="md" className="text-red">
																	{t('product_details_page:only_left', {
																		count: option.availableQuantity,
																	})}
																</PlentyBody>
															) : option.availableQuantity === 0 ? (
																<PlentyBody size="md">
																	{t('product_details_page:out_of_stock')}
																</PlentyBody>
															) : null}

															{option.variant?.lowestPrice &&
																option.availableQuantity > 0 && (
																	<div className="flex items-center gap-1">
																		{isSizeOnSaleInStore(
																			option.label,
																			selectedChannelId,
																		) &&
																		option.variant.lowestPrice.discounted ? (
																			// Item is ON SALE in this store AND has discount data -> Show SALE format
																			<>
																				<PlentyBody
																					size="md"
																					className="text-red-90"
																				>
																					{t('product_details_page:price_from')}{' '}
																					{moneyFormat(
																						centPriceToPrice(
																							option.variant.lowestPrice
																								.discounted.value.centAmount,
																							2,
																						),
																						'DKK',
																						'da-DK',
																					)}
																				</PlentyBody>
																				<PlentyBody
																					size="md"
																					className="text-black-60 line-through"
																				>
																					{moneyFormat(
																						centPriceToPrice(
																							option.variant.lowestPrice.value
																								.centAmount,
																							2,
																						),
																						'DKK',
																						'da-DK',
																					)}
																				</PlentyBody>
																			</>
																		) : (
																			// Item is NOT on sale in this store (or missing discount data) -> Show REGULAR price
																			<PlentyBody size="md">
																				{moneyFormat(
																					centPriceToPrice(
																						option.variant.lowestPrice.value
																							.centAmount,
																						2,
																					),
																					'DKK',
																					'da-DK',
																				)}
																			</PlentyBody>
																		)}
																	</div>
																)}
														</div>
													)}
												</div>
											</div>
											{isLoading && (
												<div className="absolute inset-0 flex items-center justify-center bg-white/50">
													<LoadingSpinner />
												</div>
											)}
										</div>
									)
								},
							)}
						</div>
					</div>
				</ModalContent>
			</Modal>
		</>
	)
}

export default ChooseStoreMobileModal
